import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import { parseError } from '@/hooks/useTeams';
import type { TeamForUserDetail } from '@/types/users';

import type { PersonResult, TeamsResponse } from './accounts.types';

interface AdminRequest {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

export const PAGE_SIZE = 50;

export function useAdminFetch() {
  const { getAccessTokenSilently } = useAuth0();
  return async <T>({
    path,
    method = 'GET',
    body,
  }: AdminRequest): Promise<T> => {
    const res = await apiFetch(`${API_URL}/admin${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${await getAccessTokenSilently()}`,
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<T>;
  };
}

function useAdminMutation<TVars, TResult = unknown>(
  toRequest: (vars: TVars) => AdminRequest
) {
  const adminFetch = useAdminFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => adminFetch<TResult>(toRequest(vars)),
    onSuccess: () => {
      for (const key of ['teams', 'user', 'users', 'people']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export function useTeamsList(page: number, search: string) {
  const adminFetch = useAdminFetch();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (search) params.set('search', search);
  return useQuery({
    queryKey: ['teams', 'list', params.toString()],
    queryFn: () => adminFetch<TeamsResponse>({ path: `/teams?${params}` }),
  });
}

export function useTeam(teamId: string) {
  const adminFetch = useAdminFetch();
  return useQuery({
    queryKey: ['teams', teamId],
    queryFn: () => adminFetch<TeamForUserDetail>({ path: `/teams/${teamId}` }),
    retry: false,
  });
}

export const useAddTeamMembers = (teamId: string) =>
  useAdminMutation((vars: { emails: string[]; tier: string }) => ({
    path: `/teams/${teamId}/members`,
    method: 'POST',
    body: vars,
  }));

export const useResendTeamInvite = (teamId: string) =>
  useAdminMutation((membershipId: string) => ({
    path: `/teams/${teamId}/members/${membershipId}/resend-invite`,
    method: 'POST',
  }));

export const useChangeTeamMemberTier = (teamId: string) =>
  useAdminMutation((vars: { membershipId: string; tier: string }) => ({
    path: `/teams/${teamId}/members/${vars.membershipId}/tier`,
    method: 'PATCH',
    body: { tier: vars.tier },
  }));

export const useRemoveTeamMember = (teamId: string) =>
  useAdminMutation((membershipId: string) => ({
    path: `/teams/${teamId}/members/${membershipId}`,
    method: 'DELETE',
  }));

export const useExtendTeamTrial = (teamId: string) =>
  useAdminMutation((trialEnd: string) => ({
    path: `/teams/${teamId}/extend-trial`,
    method: 'POST',
    body: { trial_end: trialEnd },
  }));

export const usePaymentLink = (ownerPath: string) =>
  useAdminMutation<boolean, { url: string }>((email) => ({
    path: `${ownerPath}/payment-link`,
    method: 'POST',
    body: { email },
  }));

export interface AddPeopleVars {
  emails: string[];
  plan: string;
  trial_end: string;
  dry_run: boolean;
}

export const useAddPeople = () =>
  useAdminMutation<AddPeopleVars, { results: PersonResult[] }>((vars) => ({
    path: '/people',
    method: 'POST',
    body: vars,
  }));

export function usePeopleDryRun(vars: Omit<AddPeopleVars, 'dry_run'>) {
  const adminFetch = useAdminFetch();
  return useQuery({
    queryKey: ['people', 'dry-run', vars],
    queryFn: () =>
      adminFetch<{ results: PersonResult[] }>({
        path: '/people',
        method: 'POST',
        body: { ...vars, dry_run: true },
      }),
    enabled: vars.emails.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
